/**
 * Texte der Lieferverträge im Web (Plan „Rollen am Geschäftspartner und Lieferantenverträge“, Aufgabe 13; Spec „Texte“):
 * eine Tabelle mit englischen Schlüsseln, kein Text im JSX. Die Fehlersätze der API stehen in der Domäne
 * (`supplierContractRules.ts`); hier stehen nur die Sätze, die das Web VOR dem Senden selbst prüft (Pflicht, Zahlform,
 * Grenzen des Schemas), weil web die Domäne nicht importiert. Zahlweisen und Steuerfälle stehen nur hier — die
 * Eingangsrechnungen nutzen dieselben Wörter (A-K3).
 */
import { monthName } from "../../format.js";
import { TERM_UNIT_TEXT } from "../../components/laufzeitPaar.js";

export const SUPPLIER_TEXT_WEB = {
  tab: "Lieferverträge",
  area: "Lieferverträge",
  /** Kurzlabel des Reiters am Telefon (Entscheid 522); steckt im langen Namen (Label in Name). */
  areaShort: "Verträge",
  /** Der Navigationseintrag über beiden Lieferantenbereichen und der Name seiner Reiterleiste (Entscheid 516b). */
  purchasingArea: "Einkauf",
  purchasingTabs: "Bereiche des Einkaufs",
  newContract: "Neuer Liefervertrag",
  edit: "Bearbeiten",
  editTitle: (title: string): string => `${title} bearbeiten`,
  changeTerm: "Kondition ändern ab …",
  changeTermTitle: (title: string): string => `Kondition ändern: ${title}`,
  terminate: "Kündigung erfassen",
  head: "Kopfdaten",
  terms: "Konditionen",
  charges: "Fälligkeiten",
  files: "Dateien",
  from: (d: string): string => `ab ${d}`,
  perCycle: { MONTHLY: "€ / Monat", QUARTERLY: "€ / Quartal", HALF_YEARLY: "€ / Halbjahr", YEARLY: "€ / Jahr" },
  state: {
    PLANNED: "geplant", RUNNING: "laufend", TERMINATED: (bis: string): string => `gekündigt zum ${bis}`, ENDED: "beendet",
  },
  billingMode: {
    INVOICE_PER_PERIOD: "Rechnung je Zeitraum", FIXED_PAYMENT: "Fester Betrag ohne Rechnung",
    INSTALMENT_SETTLEMENT: "Abschläge mit Abrechnung",
  },
  paymentMethod: { TRANSFER: "Überweisung", DIRECT_DEBIT: "Lastschrift", STANDING_ORDER: "Dauerauftrag", CARD: "Karte" },
  /** Die Vorschau der Maske (Spec „Anker der Fälligkeiten“, P20). */
  effective: (ab: string, faellig: string): string => `wirksam ab ${ab}, erster Posten mit neuem Betrag fällig ${faellig}`,
  /** Die Vorschau, wenn der Plan für die Kondition (noch) keinen Posten anlegt (jenseits des Horizonts, Fix-Runde 1 22b W2). */
  effectiveNoCharge: (ab: string): string => `wirksam ab ${ab}; im Fälligkeitenplan noch kein Posten`,
  outsideTerm: "außerhalb der Laufzeit",
  invoiceExpected: "Rechnung erwartet",
  /** Der Satz der Zahlungsfrage — Betrag und Datum stehen darin (Spec „Akte eines Lieferanten“). */
  confirmPayment: (betrag: string, datum: string): string => `Zahlung über ${betrag} am ${datum} bestätigen?`,
  /** Solange das Betragsfeld leer ist (kein „Zahlung über  am …“). */
  confirmPaymentWithoutAmount: (datum: string): string => `Zahlung am ${datum} bestätigen?`,
  /** Ein offenes Guthaben kommt als negativer Betrag zurück (Eingangsrechnung, A-K9). */
  confirmRefund: (betrag: string, datum: string): string => `Erstattung über ${betrag} erhalten am ${datum}?`,
  /** Betrag 0 ist der Widerspruch (Lastschrift zurückgegangen, Ruling Re-Review 13; Vertrag und Eingangsrechnung) — der
   *  Satz nennt, was zurückging (A-W5). */
  confirmContradiction: (betrag: string, datum: string): string =>
    `Widerspruch erfassen: Zahlung über ${betrag} zurückgegangen am ${datum}?`,
  contradictionHint: "Betrag 0 erfasst einen Widerspruch (Lastschrift zurückgegangen).",
  /** Bei einer weiteren Zahlung: was schon bezahlt ist und welche Summe danach gespeichert wird. */
  paidSoFar: (bisher: string, danach: string): string => `bisher bezahlt ${bisher} · danach ${danach}`,
  paymentSumTooLarge: "Mit dieser Zahlung hätte die Summe mehr als zehn Stellen vor dem Komma.",
  confirmTerminate: (bis: string): string => `Kündigung zum ${bis} erfassen?`,
  confirmDeleteTerm: (ab: string): string => `Kondition ab ${ab} löschen?`,
  noticeDeadline: (d: string): string => `Kündigungsfrist endet am ${d}`,
  chips: { RUNNING: "laufend", TERMINATED: "gekündigt", ENDED: "beendet" },
  empty: "Keine Lieferverträge.",
  minimumTerm: "Mindestlaufzeit",
  renewal: "Verlängerung",
  termUnit: TERM_UNIT_TEXT,
  locked: "Lieferantendaten sind der Leitung und der Verwaltung vorbehalten.",
  /** Neuer Vertrag bzw. neue Eingangsrechnung im aktiven Unternehmen ohne Lieferantenrolle (A-W6). */
  noSupplierRole: (kuerzel: string): string =>
    `Bei ${kuerzel} ist diese Firma kein Lieferant – die Rolle unter Stammdaten › Rollen je Unternehmen setzen.`,
  /** Der Satz über einem Vertrag, den ein Sprung aus dem Bereich aufgeschlagen hat (A-W2; Übersicht und Dateien wie die
   *  Belegreiter, `jumpNote`). */
  jumpFromArea: "Aus dem Bereich Lieferverträge geöffnet.",
  /** Ticket aus dem Vertrag (Spec „Glocke“, Aufgabe 22); die Beschriftung des Bezugs wie im Ticket (Aufgabe 20). */
  createTicket: "Ticket anlegen",
  ticketReference: (title: string): string => `Liefervertrag ${title}`,

  // --- Vertragsmaske ---
  sectionContract: "Vertrag",
  sectionRuntime: "Laufzeit und Kündigung",
  sectionAssignment: "Kontierung",
  sectionFirstTerm: "Erste Kondition",
  contractType: "Vertragsart",
  contractTitle: "Titel",
  reference: "Referenz beim Lieferanten",
  validFrom: "Vertragsbeginn",
  validTo: "Vertragsende",
  noticePeriod: "Kündigungsfrist",
  unitOf: (label: string): string => `Einheit: ${label}`,
  noticeReferenceLabel: "Kündigung wirkt",
  /** IMMEDIATE (Entscheid 544, Wortlaut Fixrunde 1): jederzeit, wirksam mit Ablauf der Frist ab dem Kündigungstag — 0 Tage
   *  zum Tagesende. */
  noticeReference: { TERM_END: "zum Ende der Laufzeit", CALENDAR_YEAR_END: "zum Ende des Kalenderjahres",
    IMMEDIATE: "jederzeit" },
  billingModeLabel: "Abrechnungsweise",
  paymentMethodLabel: "Zahlweise",
  settlementMonth: "Abrechnungsmonat",
  month: monthName,
  // Planbeginn bei Altverträgen (Dirk 511): Erfassungsmonat des Fälligkeitenplans.
  planFrom: "Fälligkeitenplan ab (Monat)",
  planFromYear: "Jahr",
  planFromDefault: "Vorgabe",
  /** Unter dem Paar Monat/Jahr (A-W4): `ab` ist der wirksame Tag — der Beginn, frühestens `earliest`. */
  planFromHint: (ab: string, earliest: string): string => `Leer: ab ${ab} (Vertragsbeginn, frühestens ${earliest}).`,
  planFromFact: "Fälligkeitenplan ab",
  planFromMonth: (m: number, year: string): string => `${monthName(m)} ${year}`,
  planFromDefaultLine: (ab: string): string => `${ab} (Vorgabe)`,
  note: "Notiz",
  choose: "– bitte wählen –",
  inactiveType: (name: string): string => `${name} (deaktiviert)`,

  // --- Kondition ---
  termFrom: "Gültig ab",
  currentTermLine: (betrag: string, ab: string): string => `Geltende Kondition: ${betrag} ab ${ab}`,
  cycle: "Rhythmus",
  cycles: { MONTHLY: "monatlich", QUARTERLY: "vierteljährlich", HALF_YEARLY: "halbjährlich", YEARLY: "jährlich" },
  dueRuleLabel: "Fälligkeit",
  dueRules: { SUBPERIOD_START: "zu Beginn des Zeitraums", SUBPERIOD_END: "zum Ende des Zeitraums", FIXED_DAY: "an einem festen Tag" },
  dueDay: "Tag im Monat",
  dueMonth: "Monat der Fälligkeit",
  dueOffset: "Versatz",
  days: "Tage",
  dueOnDay: (tag: number, monat: string | null): string => (monat === null ? `am ${String(tag)}.` : `am ${String(tag)}. ${monat}`),
  offsetText: (n: number): string => `${n > 0 ? "+" : "−"}${String(Math.abs(n))} ${Math.abs(n) === 1 ? "Tag" : "Tage"}`,
  netAmount: "Betrag netto",
  taxCaseLabel: "Steuerfall",
  taxCase: {
    DOMESTIC: "Vorsteuer Inland", REVERSE_CHARGE: "§ 13b (Reverse Charge)", INTRA_EU: "innergemeinschaftlich",
    EXEMPT: "steuerfrei",
  },
  taxRate: "Steuersatz",
  inactiveRate: "bisheriger Satz (nicht mehr aktiv)",
  storedRate: "gespeicherter Satz",
  termNote: "Notiz zur Kondition",
  current: "geltend",
  deleteTerm: (ab: string): string => `Kondition ab ${ab} löschen`,
  deleteAction: "Löschen",
  deleteConsequence: "Ihre offenen Posten entfallen; die vorige Kondition gilt weiter.",

  // --- Fälligkeiten ---
  columnPeriod: "Zeitraum",
  columnDue: "fällig",
  columnGross: "Brutto",
  columnPayment: "Zahlstatus",
  columnDocument: "Beleg",
  payAction: "Zahlung bestätigen",
  payFor: (zeitraum: string): string => `Zahlung für ${zeitraum} bestätigen`,
  paidOn: "Bezahlt am",
  paidAmount: "Betrag",
  euro: "€",
  paidLine: (betrag: string, datum: string): string => `${betrag} am ${datum}`,
  cancelledCount: (n: number): string => (n === 1 ? "1 entfallener Posten" : `${String(n)} entfallene Posten`),
  noCharges: "Keine Posten.",
  recalculate: "Neu berechnen",
  confirmRecalculate: "Fälligkeiten neu berechnen?",
  recalculateText: "Offene Posten entstehen neu aus den Konditionen; gebundene und bezahlte bleiben.",
  /** „Zahlung zurücknehmen“ (Aufgabe 33) — am Posten und an der Eingangsrechnung dieselbe gestapelte Frage: Die
   *  gespeicherte Zahlung fällt weg, der Zahlstatus folgt wieder Fälligkeit und Zahlweise. */
  withdrawAction: "Zahlung zurücknehmen",
  withdrawFor: (zeitraum: string): string => `Zahlung für ${zeitraum} zurücknehmen`,
  confirmWithdraw: (betrag: string, datum: string): string => `Zahlung über ${betrag} vom ${datum} zurücknehmen?`,
  /** Eine gespeicherte Bestätigung mit Betrag 0 ist der Widerspruch — die Frage nennt ihn, nicht „Zahlung über 0,00 €“. */
  confirmWithdrawContradiction: (datum: string): string => `Widerspruch vom ${datum} zurücknehmen?`,
  withdrawText: "Die erfasste Angabe wird gelöscht. Danach folgt der Zahlstatus wieder Fälligkeit und Zahlweise: "
    + "angenommen bezahlt bei Lastschrift, Dauerauftrag und Karte nach Fälligkeit, sonst offen.",
  withdrawConfirm: "Zurücknehmen",

  // --- Kündigung ---
  terminatedOn: "Gekündigt am",
  terminatedOnLine: (d: string): string => `gekündigt am ${d}`,
  terminateConsequence: "Posten nach dem Vertragsende entfallen; gebundene und bezahlte bleiben stehen.",

  // --- Liste und Bereich ---
  columnRuntime: "Laufzeit",
  columnState: "Status",
  runtime: (von: string, bis: string | null): string => (bis === null ? `ab ${von}` : `${von} – ${bis}`),
  nextDue: (datum: string, betrag: string): string => `${datum} · ${betrag}`,
  /** Das „⋯“ am Vertrag (Anatomie Teil 15): sein zugänglicher Name. */
  actionsFor: (title: string): string => `Aktionen zu Liefervertrag ${title}`,
  /** Das Objektpanel (F13): 404 und andere Ladefehler. */
  missing: "Diesen Liefervertrag gibt es nicht (mehr), oder er ist nicht sichtbar.",
  loadOneFailed: "Der Liefervertrag konnte nicht geladen werden.",
  allStates: "Alle",
  statesLabel: "Lieferverträge nach Status",
  companiesLabel: "Lieferverträge nach Unternehmen",
  none: "–",
  pending: "…",

  // --- Knöpfe, Zustände, Prüfungen der Maske ---
  save: "Speichern",
  cancel: "Abbrechen",
  confirm: "Bestätigen",
  loading: "Lädt …",
  loadFailed: "Die Lieferverträge konnten nicht geladen werden.",
  chargesLoadFailed: "Die Fälligkeiten konnten nicht geladen werden.",
  typesLoadFailed: "Die Vertragsarten konnten nicht geladen werden.",
  ratesLoadFailed: "Die Steuersätze konnten nicht geladen werden.",
  namesLoadFailed: "Die Namen von Konto und Kostenstelle konnten nicht geladen werden.",
  actionFailed: "Das hat nicht geklappt.",
  reloadOnClose: "Schließen lädt den aktuellen Stand.",
  dateRequired: "Bitte ein Datum eintragen.",
  choiceRequired: "Bitte auswählen.",
  amountInvalid: "Ein Betrag hat höchstens zehn Stellen vor und zwei nach dem Komma und ist nicht negativ.",
  amountInvalidSigned: "Ein Betrag hat höchstens zehn Stellen vor und zwei nach dem Komma.",
  wholeNumber: (min: number, max: number): string => `Eine ganze Zahl von ${String(min)} bis ${String(max)}.`,
} as const;
